<script setup lang="ts">
import _ from 'lodash'
import type Filter from '@/types/filter'
import { useProjectStore } from '@/store/project'
import { useBuildingStore } from '@/store/building'
import { useCustomFields } from '@/composables/useCustomFields'

import FilterSlider from './FilterSlider.vue'
import FilterBoolean from './FilterBoolean.vue'
import FilterTextField from './FilterTextField.vue'
import FilterComboBox from './FilterComboBox.vue'
import FilterCheckboxes from './FilterCheckboxes.vue'
import FilterToggleButtons from './FilterToggleButtons.vue'
import FilterDate from './FilterDate.vue'
import { useFilterStore } from '@/store/filter'

const { renderer } = useCustomFields()
const projectStore = useProjectStore()
const buildingStore = useBuildingStore()
const { projectWithFieldsAndStatuses: project } = projectStore
const filtersStore = useFilterStore()

const props = defineProps({
    filter: {
        type: Object as PropType<Filter>,
        required: true
    },
    countAppliedFilters: {
        type: Number,
        default: 0
    }
})

const conditionsInUrlToArray = (value) => {
    if (typeof value === 'string') {
        return [value]
    }
    return value
}

const updateFilter = (field, payload) => {
    filtersStore.updateFilter(field, payload)
}

const { $getI18n } = useNuxtApp()

const renderValue = (project, field, value) => {
    return renderer(project, field.type, field.key, value)
}
</script>
<template>
    <div class="filter-wrapper">
        <template v-if="filtersStore.displayToggleButtons(filter)">
            <div v-if="filter.allValues.length > 1" class="my-3">
                <span class="text-subtitle-2"> {{ $getI18n(filter.field.label) }} </span>

                <FilterToggleButtons
                    @updateFilter="updateFilter(filter.field, $event)"
                    :initialValue="conditionsInUrlToArray(filter.conditionsInUrl)"
                    :items="
                        filter.allValues.map((item) => {
                            return {
                                id: item,
                                name: `${item}`
                            }
                        })
                    "
                />
            </div>
        </template>
        <template v-else-if="['number', 'currency', 'area'].includes(filter.field.type)">
            <div v-if="_.uniq(filter.allValues).length > 1">
                <FilterSlider
                    :renderer="(e) => renderValue(project, filter.field, e)"
                    @updateFilter="updateFilter(filter.field, $event)"
                    :initialValue="filter.conditionsInUrl"
                    :min="_.first(filter.allValues)"
                    :max="_.last(filter.allValues)"
                    :step="filter.field.type === 'currency' ? 10000 : 1"
                    :label="filter.field.label"
                />
            </div>
        </template>
        <template v-else-if="['boolean'].includes(filter.field.type)">
            <div class="filter-title d-flex justify-space-between">
                <span class="text-subtitle-2"> {{ $getI18n(filter.field.label) }} </span>
            </div>
            <div>
                <FilterBoolean
                    @updateFilter="updateFilter(filter.field, $event)"
                    :initialValue="conditionsInUrlToArray(filter.conditionsInUrl)"
                />
            </div>
        </template>
        <template v-else-if="['string', 'html', 'url'].includes(filter.field.type)">
            <div class="filter-title d-flex justify-space-between">
                <span class="text-subtitle-2"> {{ $getI18n(filter.field.label) }} </span>
            </div>
            <div>
                <FilterTextField
                    @updateFilter="updateFilter(filter.field, $event)"
                    :initialValue="filter.conditionsInUrl"
                />
            </div>
        </template>
        <template v-else-if="['building'].includes(filter.field.key)">
            <div v-if="_.uniq(filter.allValues).length > 1">
                <div class="filter-title d-flex justify-space-between">
                    <span class="text-subtitle-2"> {{ $getI18n(filter.field.label) }} </span>
                </div>
                <FilterComboBox
                    @updateFilter="updateFilter(filter.field, $event)"
                    :initialValue="conditionsInUrlToArray(filter.conditionsInUrl)"
                    :label="$getI18n(filter.field.label)"
                    :items="
                        filter.allValues.map((id) => {
                            return {
                                id: id,
                                name: buildingStore.getBuildingById(id).name
                            }
                        })
                    "
                />
            </div>
        </template>
        <template v-else-if="['status'].includes(filter.field.key)">
            <div v-if="_.uniq(filter.allValues).length > 1">
                <div class="filter-title d-flex justify-space-between">
                    <span class="text-subtitle-2"> {{ $getI18n(filter.field.label) }} </span>
                </div>
                <FilterCheckboxes
                    @updateFilter="updateFilter(filter.field, $event)"
                    :initialValue="conditionsInUrlToArray(filter.conditionsInUrl)"
                    :items="
                        filter.allValues.map((id) => {
                            return {
                                id: id,
                                name: renderer(project, 'status', 'status', id)
                            }
                        })
                    "
                />
            </div>
        </template>
        <template v-else-if="['date'].includes(filter.field.key)">
            <div v-if="_.uniq(filter.allValues).length > 1">
                <div class="filter-title d-flex justify-space-between">
                    <span class="text-subtitle-2"> {{ $getI18n(filter.field.label) }} </span>
                </div>
                <FilterDate
                    @updateFilter="updateFilter(filter.field, $event)"
                    :initialValue="filter.conditionsInUrl"
                />
            </div>
        </template>
        <template v-else>
            <div class="filter-title d-flex justify-space-between">
                <span class="text-subtitle-2"> {{ $getI18n(filter.field.label) }} </span>
            </div>
            <FilterTextField
                @updateFilter="updateFilter(filter.field, $event)"
                :initialValue="filter.conditionsInUrl"
            />
        </template>
    </div>
</template>
